// src/features/owner/charts/Donut.tsx — SVG donut chart with centre total + empty state.
import styled, { useTheme } from 'styled-components';
import type { DistributionSlice } from '@/core/models';

const Empty = styled.p`
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;
  padding: 24px 0;
`;

export function Donut({
  slices,
  total,
  ariaLabel,
}: {
  slices: DistributionSlice[];
  total: number;
  ariaLabel: string;
}) {
  const theme = useTheme();

  const TONES: Record<string, string> = {
    brand: theme.color.brandFrom,
    violet: theme.color.brandTo,
    mint: theme.color.success,
    ember: theme.color.accentAmber,
  };

  if (!total) return <Empty>Not enough data yet</Empty>;

  // Each arc starts at 12 o'clock (offset 25) minus the cumulative % of prior arcs.
  // Computed functionally (prefix-sum via reduce) to avoid mutating a render-scoped value.
  const positive = slices.filter((s) => s.value > 0);
  return (
    <svg viewBox="0 0 42 42" width="104" height="104" role="img" aria-label={ariaLabel}>
      <circle cx="21" cy="21" r="15.9" fill="none" stroke={theme.color.glassBorder} strokeWidth="6" />
      {positive.map((s, i) => {
        const pct = (s.value / total) * 100;
        const priorPct = positive
          .slice(0, i)
          .reduce((sum, p) => sum + (p.value / total) * 100, 0);
        return (
          <circle
            key={s.label}
            cx="21"
            cy="21"
            r="15.9"
            fill="none"
            stroke={TONES[s.tone] ?? theme.color.brandFrom}
            strokeWidth="6"
            strokeDasharray={`${pct} ${100 - pct}`}
            strokeDashoffset={25 - priorPct}
          />
        );
      })}
      <text
        x="21"
        y="22"
        textAnchor="middle"
        fontSize="7"
        fontWeight="700"
        fill="currentColor"
      >
        {total}
      </text>
    </svg>
  );
}
